import React, { useMemo, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { ImportJob, SOURCE_LABELS } from '../services/importService';
import {
  parseChecklistState, buildChecklistItems, saveChecklistState, ImportChecklistState,
} from '../services/importChecklistService';
import BoardModal, { BMCancelButton } from './BoardModal';

export const importChecklistDismissKey = (jobId: string) => `dagrun_pm_import_checklist_dismissed_${jobId}`;

interface Props {
  pca: IPublicClientApplication;
  job: ImportJob;
  mode: 'panel' | 'modal';
  onClose: () => void;   // panel: dismiss · modal: close
}

/**
 * Post-import verification checklist. Standard items always show; conditional
 * items only when the captured summary flags them relevant. The checked map
 * is persisted per import job (dagrun_pm_importsummaryjson), so ticks survive
 * reloads and are shared between the board panel and the Import-tab modal.
 */
export default function ImportChecklistPanel({ pca, job, mode, onClose }: Props) {
  const [state, setState] = useState<ImportChecklistState>(() => parseChecklistState(job));
  const items = useMemo(() => buildChecklistItems(state.summary), [state.summary]);
  const doneCount = items.filter(i => state.checked[i.key]).length;

  const toggle = (key: string) => {
    setState(prev => {
      const next: ImportChecklistState = { ...prev, checked: { ...prev.checked, [key]: !prev.checked[key] } };
      // Persist best-effort; local state is the source of truth for the UI.
      saveChecklistState(pca, job.dagrun_pm_importjobid, next).catch(() => {});
      return next;
    });
  };

  const body = (
    <div>
      <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 12 }}>
        {SOURCE_LABELS[job.dagrun_pm_sourcetype] ?? 'Import'} · {doneCount} of {items.length} verified
        <div style={{ background: 'var(--surface-2)', borderRadius: 3, height: 4, overflow: 'hidden', marginTop: 6 }}>
          <div style={{ height: '100%', width: `${items.length ? (doneCount / items.length) * 100 : 0}%`, background: 'var(--brand)' }} />
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {items.map(it => {
          const checked = !!state.checked[it.key];
          return (
            <label key={it.key}
              style={{
                display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 10px', borderRadius: 6,
                cursor: 'pointer', background: checked ? 'var(--surface-2)' : 'transparent',
                border: '0.5px solid var(--border-light)',
              }}>
              <input type="checkbox" checked={checked} onChange={() => toggle(it.key)} style={{ marginTop: 2 }} />
              <span style={{ flex: 1 }}>
                <span style={{ fontSize: 13, color: 'var(--ink)', fontWeight: 500, textDecoration: checked ? 'line-through' : 'none' }}>
                  {it.label}
                  {it.conditional && (
                    <span style={{ fontSize: 10, color: 'var(--brand)', background: 'var(--brand-mist)', borderRadius: 8, padding: '1px 6px', marginLeft: 8, fontWeight: 600 }}>
                      for this import
                    </span>
                  )}
                </span>
                {it.detail && <span style={{ display: 'block', fontSize: 12, color: 'var(--ink-4)', marginTop: 2 }}>{it.detail}</span>}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );

  if (mode === 'modal') {
    return (
      <BoardModal open title="Migration checklist" width={560} onClose={onClose}
        footer={<BMCancelButton onClick={onClose}>Close</BMCancelButton>}>
        {body}
      </BoardModal>
    );
  }

  // Panel (board) — dismissable card.
  return (
    <div className="card" style={{ padding: 16, marginBottom: 16, borderLeft: '3px solid var(--brand)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div className="eyebrow" style={{ margin: 0 }}>POST-IMPORT VERIFICATION</div>
        <button type="button" onClick={onClose} aria-label="Dismiss"
          style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--ink-4)', fontSize: 16 }}>×</button>
      </div>
      {body}
      <div style={{ marginTop: 10, fontSize: 11, color: 'var(--ink-4)' }}>
        You can reopen this from the Import tab → “View migration checklist”.
      </div>
    </div>
  );
}
